import {
  BN,
  compareLowerStr,
  formatDecimal,
  isNormalPositive,
} from '@bifrost-platform/bifront-sdk-react-wallet';
import { motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';
import IconArrow from '@/assets/icons/icon-arrow.svg';
import IconMinus from '@/assets/icons/icon-filled-minus.svg';
import IconPlus from '@/assets/icons/icon-filled-plus.svg';
import Slider from '@/components/Slider';
import { FlexBox } from '@/components/atom/FlexBox';
import { GlassPanel } from '@/components/atom/GlassPanel';
import Input from '@/components/atom/Input';
import { StyledText } from '@/components/atom/Text';
import { MEDIA_QUERY_BREAKPOINT } from '@/configs/mediaQuery';
import { TOKEN_SYMBOL_BTCUSD } from '@/configs/tokens/tokenSymbols';
import useBifi from '@/hooks/api/bifi/useBifi';
import useEverdex from '@/hooks/api/everdex/useEverdex';
import useInitializer from '@/hooks/bifrontSdk/useInitializer';
import useGetCurrencyString from '@/hooks/store/useGetCurrencyString';
import useEnv from '@/hooks/useEnv';
import {
  formatAmount,
  formatAmountOnHover,
  formatRate,
  shortenNotation,
} from '@/libs/formatNumber';
import { dashboardCardVariant } from '@/styles/motion';
import { parseNumberInput } from '@/utils/input';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '../atom/Tooltip';

const InputWrapper = styled(FlexBox)`
  justify-content: space-between;

  & > input {
    min-width: 238px;
    width: 100%;
  }

  input {
    &:last-child {
      flex-grow: 0;
    }
  }

  @media (${MEDIA_QUERY_BREAKPOINT}) {
    & > input {
      width: auto;
      flex: 1;
    }
  }
`;

const EstimatedCard = styled(GlassPanel)`
  flex: 1;
  min-height: 160px;
  padding: 24px 16px;
  min-width: 240px;
`;

const StyledFlexBox = styled(FlexBox)`
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: 364px;

  @media (${MEDIA_QUERY_BREAKPOINT}) {
    max-width: 100%;
    flex: 1;
  }
`;

const Container = styled(GlassPanel)`
  flex: 1;
  & > *:nth-child(2) > div {
    display: grid;
    grid-template: 'a b';
    grid-template-columns: 1fr 1fr;
  }

  @media (${MEDIA_QUERY_BREAKPOINT}) {
    width: 100%;
  }
`;

const MonthPanel = styled(GlassPanel)`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 116px;
  padding: ${(p) => p.theme.size.md};
  gap: ${(p) => p.theme.size.xxs};
  font-size: ${(p) => p.theme.size.lg};
  ${(p) => p.theme.font.bold}
  color: ${(p) => p.theme.colors.white};

  input {
    width: 24px;
    flex-grow: 0;
    outline: none;
    background-color: transparent;
    text-align: center;
  }

  @media (${MEDIA_QUERY_BREAKPOINT}) {
    width: auto;
  }
`;

const TitleContainer = styled(FlexBox)<{
  isLayoutChange: boolean;
  isShow: boolean;
}>`
  cursor: ${(p) => (p.isLayoutChange ? 'pointer' : 'unset')};
  svg {
    ${(p) => p.theme.transition.default};
    transition-duration: 0.4s;
    ${(p) => (p.isShow ? 'transform: rotate(180deg);' : '')};
  }
`;

export default function ProfitCalculatePanel() {
  // translate
  const { t } = useTranslation('dashboard');

  // initializer
  const { syncedBifi, syncedEverdex } = useInitializer();

  // env
  const { tokenBifrostBtcusd, tokenBifrostEverdexStakeLp } = useEnv();

  // bifi
  const {
    lendingStatuses,
    rewardApr: bifiApr,
    isLoading: isLoadingBifi,
  } = useBifi();

  // everdex
  const {
    lpBalance,
    depositedLpAmount,
    poolApr: everdexPoolApr,
    lpRewardApr: everdexLpApr,
    isLoading: isLoadingEverdex,
  } = useEverdex();

  // currency
  const {
    formatCurrencyValue,
    parseCurrencyValue,
    getCurrencyString,
    currencyId,
    currencySymbol,
  } = useGetCurrencyString();

  // state
  const [depositRatio, setDepositRatio] = useState(0);
  const [amount, setAmount] = useState('');
  const [period, setPeriod] = useState(12);
  const [isShowCalculator, setIsShowCalculator] = useState(true);
  const [hasBeenUpdated, setHasBeenUpdated] = useState(false);

  const isLayoutChange = useMediaQuery({
    query: `(max-width: 1330px)`,
  });

  // memo
  const everdexApr = useMemo(
    () =>
      new BN(isNormalPositive(everdexPoolApr, true) ? everdexPoolApr : 0).plus(
        isNormalPositive(everdexLpApr, true) ? everdexLpApr : 0
      ),
    [everdexLpApr, everdexPoolApr]
  );
  const bifiRateApr = useMemo(
    () =>
      new BN(isNormalPositive(bifiApr, true) ? bifiApr : 0).multipliedBy(
        new BN(depositRatio).multipliedBy(0.01)
      ),
    [bifiApr, depositRatio]
  );
  const everdexRateApr = useMemo(
    () =>
      new BN(isNormalPositive(everdexApr, true) ? everdexApr : 0).multipliedBy(
        new BN(100 - depositRatio).multipliedBy(0.01)
      ),
    [depositRatio, everdexApr]
  );
  const apr = useMemo(
    () =>
      bifiRateApr
        .plus(everdexRateApr)
        .div(12 / (isNormalPositive(period, true) ? period ?? 0 : 0)),
    [bifiRateApr, everdexRateApr, period]
  );
  const profitAmount = useMemo(
    () =>
      new BN(isNormalPositive(amount, true) ? amount ?? 0 : 0).multipliedBy(
        apr.div(100)
      ),
    [amount, apr]
  );
  const isNormalInputed = useMemo(
    () => isNormalPositive(period, true) && isNormalPositive(amount, true),
    [amount, period]
  );
  const shortenAmount = useMemo(() => {
    const amount = getCurrencyString(profitAmount, tokenBifrostBtcusd);

    return amount.indexOf('<') >= 0
      ? amount
      : `${currencySymbol}${shortenNotation({
          currencyId,
          value: amount.substring(1, amount.length).replaceAll(',', ''),
        })}`;
  }, [
    profitAmount,
    tokenBifrostBtcusd,
    getCurrencyString,
    currencyId,
    currencySymbol,
  ]);

  // handler
  const handlePeriodClick = useCallback(
    (type: 'plus' | 'minus') => () => {
      if (type === 'minus') {
        setPeriod((prev) => (prev <= 1 ? prev : prev - 1));
      }

      if (type === 'plus') {
        setPeriod((prev) => (prev >= 12 ? prev : prev + 1));
      }
    },
    []
  );
  const handlePeriodChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    let ppp = Number(e.target.value.replace(/\D/g, ''));

    if (ppp >= 12) {
      ppp = 12;
    }

    setPeriod(ppp);
  }, []);
  const handleShowCalculator = useCallback(() => {
    if (isLayoutChange) {
      setIsShowCalculator(!isShowCalculator);
    }
  }, [isLayoutChange, isShowCalculator]);
  const handleAmountChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      setAmount(parseNumberInput(e.target.value)),
    []
  );
  const handleDepositRatioChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDepositRatio(Number(e.target.value));
    },
    []
  );

  // effect
  useEffect(() => {
    setIsShowCalculator(true);
  }, [isLayoutChange]);
  useEffect(() => {
    if (
      syncedBifi &&
      syncedEverdex &&
      !(hasBeenUpdated || isLoadingBifi || isLoadingEverdex)
    ) {
      const bifiDepositAmount =
        lendingStatuses.find(({ symbol }) =>
          compareLowerStr(symbol, TOKEN_SYMBOL_BTCUSD)
        )?.deposit?.amount ?? '0';
      const addedLiquidityAmount = formatCurrencyValue(
        parseCurrencyValue(lpBalance, tokenBifrostEverdexStakeLp),
        tokenBifrostBtcusd
      );
      const lpStakedAmount = formatCurrencyValue(
        parseCurrencyValue(depositedLpAmount, tokenBifrostEverdexStakeLp),
        tokenBifrostBtcusd
      );
      const totalAmount = formatDecimal(
        new BN(
          isNormalPositive(bifiDepositAmount, true) ? bifiDepositAmount : '0'
        )
          .plus(
            isNormalPositive(addedLiquidityAmount, true)
              ? addedLiquidityAmount
              : '0'
          )
          .plus(isNormalPositive(lpStakedAmount, true) ? lpStakedAmount : '0'),
        2
      );

      if (isNormalPositive(totalAmount, true)) {
        setAmount(totalAmount);

        setDepositRatio(
          new BN(
            isNormalPositive(bifiDepositAmount, true) ? bifiDepositAmount : '0'
          )
            .div(totalAmount)
            .multipliedBy(100)
            .toNumber()
        );

        setHasBeenUpdated(true);
      }
    }
  }, [
    depositedLpAmount,
    formatCurrencyValue,
    hasBeenUpdated,
    isLoadingBifi,
    isLoadingEverdex,
    lendingStatuses,
    lpBalance,
    parseCurrencyValue,
    syncedBifi,
    syncedEverdex,
    tokenBifrostBtcusd,
    tokenBifrostEverdexStakeLp,
  ]);

  return (
    <Container
      as={motion.div}
      variants={dashboardCardVariant}
      $padding="xl"
      $rounded="xl"
      $flex
      $direction="column"
      $gap="lg"
    >
      <TitleContainer
        $flex
        $gap="xxxs"
        $align="center"
        $justify="space-between"
        onClick={handleShowCalculator}
        isLayoutChange={isLayoutChange}
        isShow={isShowCalculator}
      >
        <StyledText as="h3" $fontSize="xl" $fontWeight="black">
          {t('apyCalculator')}
        </StyledText>
        {isLayoutChange && <IconArrow />}
      </TitleContainer>
      {isShowCalculator && (
        <FlexBox $flex $direction="column" $gap="xs">
          <StyledText
            as="label"
            htmlFor="amount"
            $fontSize="md"
            $fontWeight="medium"
            $textColor="textGray"
          >
            {t('depositAmountPeriod')}
          </StyledText>
          <FlexBox $flex $direction="row" $gap="xl">
            <StyledFlexBox $flex $direction="column" $gap="xl">
              <FlexBox $flex $direction="column" $gap="xs">
                <InputWrapper $flex $direction="row" $gap="xs">
                  <Input
                    id="amount"
                    $padding="md"
                    $textSize="lg"
                    $fontWeight="bold"
                    placeholder={t('inputAmount')}
                    value={amount}
                    onChange={handleAmountChange}
                  />
                  <MonthPanel
                    $padding="xl"
                    $rounded="lg"
                    $flex
                    $direction="column"
                    $gap="md"
                    $noBorder
                  >
                    <button type="button" onClick={handlePeriodClick('minus')}>
                      <IconMinus />
                    </button>
                    <FlexBox $gap="xxxs">
                      <input
                        id="amount"
                        placeholder="0"
                        onChange={handlePeriodChange}
                        value={period}
                      />
                    </FlexBox>
                    <button type="button" onClick={handlePeriodClick('plus')}>
                      <IconPlus />
                    </button>
                  </MonthPanel>
                </InputWrapper>
              </FlexBox>
              <FlexBox $flex $direction="column" $gap="xs">
                <StyledText
                  $fontSize="md"
                  $fontWeight="medium"
                  $textColor="textGray"
                >
                  {t('depositRatio')}
                </StyledText>
                <FlexBox $flex $direction="column" $gap="xxxs">
                  <FlexBox $flex $justify="space-between">
                    <StyledText $fontSize="sm" $fontWeight="bold">
                      BiFi: {formatAmount(depositRatio)}%
                    </StyledText>
                    <StyledText $fontSize="sm" $fontWeight="bold">
                      Everdex: {formatAmount(100 - depositRatio)}%
                    </StyledText>
                  </FlexBox>
                  <Slider
                    value={depositRatio}
                    onChange={handleDepositRatioChange}
                    isBothFill
                  />
                </FlexBox>
              </FlexBox>
            </StyledFlexBox>
            <EstimatedCard
              $padding="xl"
              $rounded="lg"
              $flex
              $direction="column"
              $gap="md"
              $noBorder
            >
              <StyledText>{t('estimatedProfitRate')}</StyledText>
              <FlexBox $flex $direction="column">
                <div>
                  <Tooltip placement="top-end" offset={14}>
                    <TooltipTrigger>
                      <StyledText
                        as="strong"
                        $fontSize="xxxl"
                        $fontWeight="bold"
                        style={{
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {shortenAmount}
                      </StyledText>
                    </TooltipTrigger>
                    <TooltipContent>
                      <TooltipArrow />
                      <StyledText $fontSize="xs" $fontWeight="medium">
                        {getCurrencyString(
                          profitAmount,
                          tokenBifrostBtcusd,
                          formatAmountOnHover
                        )}
                      </StyledText>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div>
                  <Tooltip placement="top" offset={14}>
                    <TooltipTrigger>
                      <StyledText
                        $fontSize="md"
                        $fontWeight="medium"
                        $textColor="green"
                      >
                        {formatRate(isNormalInputed ? apr : 0)}
                        <StyledText $fontSize="md" $textColor="green">
                          %
                        </StyledText>
                      </StyledText>
                    </TooltipTrigger>
                    <TooltipContent>
                      <TooltipArrow />
                      <StyledText $fontSize="xs" $fontWeight="medium">
                        {formatAmountOnHover(isNormalInputed ? apr : 0)}%
                      </StyledText>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </FlexBox>
            </EstimatedCard>
          </FlexBox>
        </FlexBox>
      )}
    </Container>
  );
}
